<template>
  <app-layout title="学校詳細">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
          {{ school.name }} - 集計状況詳細
        </h2>
        <button
          class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          @click="goBack">
          戻る
        </button>
      </div>
    </template>

    <div class="py-8">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 space-y-6">
        <!-- 基本情報 -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">基本情報</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div class="text-sm text-gray-600">学校名</div>
              <div class="text-lg font-medium">{{ school.name }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-600">模試</div>
              <div class="text-lg font-medium">{{ examination.title }}</div>
            </div>
          </div>
        </div>

        <!-- 集計状況アラート -->
        <div
          v-if="aggregationStatus.issues.length > 0"
          class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            <span class="inline-flex items-center">
              <svg
                class="w-5 h-5 text-red-500 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              問題・警告
            </span>
          </h3>

          <div class="space-y-3">
            <div
              v-for="(issue, index) in aggregationStatus.issues"
              :key="index"
              :class="issue.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'"
              class="border rounded-lg p-4">
              <div class="flex items-center">
                <div
                  :class="issue.type === 'error' ? 'text-red-400' : 'text-yellow-400'"
                  class="flex-shrink-0 mr-3">
                  <svg
                    class="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <div
                    :class="issue.type === 'error' ? 'text-red-800' : 'text-yellow-800'"
                    class="font-medium">
                    {{ issue.message }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 完了メッセージ -->
        <div
          v-else
          class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 text-green-400 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"></path>
            </svg>
            <div class="text-green-800 font-medium">
              すべての集計が正常に完了しています
            </div>
          </div>
        </div>

        <!-- 統計情報 -->
        <div
          v-if="schoolStats.examination_scores && Object.keys(schoolStats.examination_scores).length > 0"
          class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">統計情報</h3>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              v-for="(score, type) in schoolStats.examination_scores"
              :key="type"
              class="bg-gray-50 rounded-lg p-4">
              <div class="text-sm text-gray-600 mb-2">
                {{ getExaminationTypeName(type) }}
              </div>
              <div class="space-y-1">
                <div class="flex justify-between text-sm">
                  <span>最高点:</span>
                  <span class="font-medium">{{ score.highest_score }}点</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span>平均点:</span>
                  <span class="font-medium">{{ score.average_score }}点</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span>最低点:</span>
                  <span class="font-medium">{{ score.lowest_score }}点</span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="schoolStats.last_aggregation"
            class="mt-4 text-sm text-gray-500">
            最終集計日時: {{ formatDate(schoolStats.last_aggregation) }}
          </div>
        </div>

        <subject-field-analytics
          :national-analytics-ready="nationalAnalyticsReady"
          :subject-names="subjectNames"
          :question-corrent-answer-rate="questionCorrentAnswerRate"
          :subject-corrent-answer-rate="subjectCorrentAnswerRate"
          :school-subject-corrent-answer-rate="schoolSubjectCorrentAnswerRate"
          :school-question-corrent-answer-rate="schoolQuestionCorrentAnswerRate"
          :subject-field-breakdowns="subjectFieldBreakdowns" />

        <!-- 生徒一覧 -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
          <div class="p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">生徒一覧</h3>

            <!-- フィルター -->
            <div class="mb-4 flex space-x-4">
              <select
                v-model="statusFilter"
                class="border-gray-300 rounded-md shadow-sm"
                @change="filterStudents">
                <option value="">全ての状況</option>
                <option value="not_purchased">未購入</option>
                <option value="not_started">未開始</option>
                <option value="in_progress">受験中</option>
                <option value="unscored">未採点</option>
                <option value="unranked">ランキング未設定</option>
                <option value="complete">完了</option>
              </select>
            </div>

            <!-- テーブル -->
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ユーザーID
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学籍番号
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      氏名
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学年
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      開始日時
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      完了日時
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      必須<br /><span class="font-normal">(50点)</span>
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      一般<br /><span class="font-normal">(100点)</span>
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      実地<br /><span class="font-normal">(50点)</span>
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      合計<br /><span class="font-normal">(200点)</span>
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      順位
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状況
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr
                    v-for="student in filteredStudents"
                    :key="student.id">
                    <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ student.id }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ student.student_id }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {{ student.name }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {{ getGradeText(student.service_years, student.graduation_year) }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {{ student.started_at ? formatDate(student.started_at) : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {{ student.finished_at ? formatDate(student.finished_at) : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      {{ student.compulsory_score !== null && student.compulsory_score !== undefined ? student.compulsory_score : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      {{ student.general_score !== null && student.general_score !== undefined ? student.general_score : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      {{ student.practice_score !== null && student.practice_score !== undefined ? student.practice_score : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center font-medium">
                      {{ student.total_score !== null && student.total_score !== undefined ? student.total_score : '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {{ student.ranking || '-' }}
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap">
                      <span
                        :class="getStudentStatusBadgeClass(student.status)"
                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {{ getStudentStatusText(student.status) }}
                      </span>
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm">
                      <button
                        :disabled="deletingStudentId === student.id"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                        type="button"
                        @click="confirmStudentDeletion(student)">
                        CSV取込データ削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 空の状態 -->
            <div
              v-if="filteredStudents.length === 0"
              class="text-center py-12">
              <div class="text-gray-500">該当する生徒データがありません</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import { computed, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import Swal from 'sweetalert2'
import AppLayout from '@/Layouts/AppLayout'
import SubjectFieldAnalytics from './Partials/SubjectFieldAnalytics'

export default {
  components: {
    AppLayout,
    SubjectFieldAnalytics,
  },

  props: {
    school: {
      type: Object,
      required: true,
    },
    examination: {
      type: Object,
      required: true,
    },
    students: {
      type: Array,
      default: () => [],
    },
    aggregationStatus: {
      type: Object,
      default: () => ({ issues: [], overall_status: 'unknown' }),
    },
    schoolStats: {
      type: Object,
      default: () => ({}),
    },
    subjectNames: {
      type: Array,
      default: () => [],
    },
    nationalAnalyticsReady: {
      type: Boolean,
      default: false,
    },
    questionCorrentAnswerRate: {
      type: Array,
      default: () => [],
    },
    subjectCorrentAnswerRate: {
      type: Array,
      default: () => [],
    },
    schoolSubjectCorrentAnswerRate: {
      type: Array,
      default: () => [],
    },
    schoolQuestionCorrentAnswerRate: {
      type: Array,
      default: () => [],
    },
    subjectFieldBreakdowns: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    const statusFilter = ref('')
    const deletingStudentId = ref(null)

    // フィルターされた生徒一覧
    const filteredStudents = computed(() => {
      if (!statusFilter.value) return props.students
      return props.students.filter(student => student.status === statusFilter.value)
    })

    // 戻る
    const goBack = () => {
      Inertia.visit(route('admin.school-results.index', {
        examinationId: props.examination.id,
      }))
    }

    // フィルター変更
    const filterStudents = () => {
      // Computed propertyが自動的に再計算される
    }

    // 模試タイプ名
    const getExaminationTypeName = (type) => {
      const names = {
        1: '必須科目',
        2: '一般科目',
        3: '実地科目',
      }
      return names[type] || `タイプ${type}`
    }

    // 学年テキスト
    const getGradeText = (serviceYears, graduationYear = null) => {
      const grades = {
        6: '1年',
        7: '2年',
        8: '3年',
        9: '4年',
      }
      if (serviceYears === 10) {
        return graduationYear ? `${graduationYear}年度卒` : '-'
      }
      return grades[serviceYears] || `${serviceYears}年`
    }

    // 生徒ステータスバッジクラス
    const getStudentStatusBadgeClass = (status) => {
      const classes = {
        complete: 'bg-green-100 text-green-800',
        unranked: 'bg-yellow-100 text-yellow-800',
        unscored: 'bg-orange-100 text-orange-800',
        in_progress: 'bg-blue-100 text-blue-800',
        not_started: 'bg-gray-100 text-gray-800',
        not_purchased: 'bg-red-100 text-red-800',
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    // 生徒ステータステキスト
    const getStudentStatusText = (status) => {
      const texts = {
        complete: '完了',
        unranked: 'ランキング未設定',
        unscored: 'スコア未計算',
        in_progress: '受験中',
        not_started: '未開始',
        not_purchased: '未購入',
      }
      return texts[status] || '不明'
    }

    // 日付フォーマット
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const confirmStudentDeletion = (student) => {
      if (deletingStudentId.value) {
        return
      }

      Swal.fire({
        icon: 'warning',
        title: 'CSV取込データ削除',
        html: `生徒「${student.name}」のマークシート取込データを削除します。<br>この操作は元に戻せません。<br>削除してよろしいですか？`,
        showCancelButton: true,
        confirmButtonText: '削除する',
        cancelButtonText: 'キャンセル',
        focusCancel: true,
      }).then((result) => {
        if (!result.isConfirmed) {
          return
        }

        deletingStudentId.value = student.id

        Inertia.delete(route('admin.school-results.students.destroy', {
          schoolId: props.school.id,
          examinationId: props.examination.id,
          studentId: student.id,
        }), {
          preserveScroll: true,
          onFinish: () => {
            deletingStudentId.value = null
          },
        })
      })
    }

    return {
      statusFilter,
      filteredStudents,
      goBack,
      filterStudents,
      getExaminationTypeName,
      getGradeText,
      getStudentStatusBadgeClass,
      getStudentStatusText,
      formatDate,
      deletingStudentId,
      confirmStudentDeletion,
    }
  },
}
</script>
